import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Search, X, Check, Trash2, ArrowUpDown } from "lucide-react";

const AdminProduct = () => {
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("all"); // "all" or "inactive"
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        setLoading(true);
        const res = await axios.get("https://api.kamulyaan.id/books");
        setItems(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error(err);
        setError("Gagal memuat data");
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, []);

  // Filter + View mode
  const filteredItems = items.filter((item) => {
    const matchesSearch =
      (item.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.desc || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.storeid || "").toString().includes(searchTerm) ||
      (item.category || "").toLowerCase().includes(searchTerm.toLowerCase());

    const matchesView =
      viewMode === "all" || item.active === 0 || item.active === false;

    return matchesSearch && matchesView;
  });

  // Sorting logic
  const sortedItems = [...filteredItems].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    // Special handling for price (numeric)
    if (sortConfig.key === "price") {
      aValue = Number(aValue || 0);
      bValue = Number(bValue || 0);
    }

    // Special handling for active status (true/false → 1/0)
    if (sortConfig.key === "active") {
      aValue = a.active ? 1 : 0;
      bValue = b.active ? 1 : 0;
    }

    if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown size={14} />;
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  // Selection
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === sortedItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedItems.map((item) => item.id)));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  // Bulk actions (same as before - you need backend endpoints)
  const handleBulkAction = async (action) => {
    if (selectedIds.size === 0) return;
    // ... same implementation as previous version ...
    // (activate, deactivate, delete)
  };

  if (loading) return <div className="loading">Memuat data...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="admin-page">
      <div className="header">
        <h1>Admin Panel – Kelola Produk</h1>

        <div className="controls">
          <div className="view-toggle">
            <button
              className={viewMode === "all" ? "active" : ""}
              onClick={() => setViewMode("all")}
            >
              View All
            </button>
            <button
              className={viewMode === "inactive" ? "active" : ""}
              onClick={() => setViewMode("inactive")}
            >
              View Inactive
            </button>
          </div>

          <div className="search-box">
            <Search size={18} color="#888" />
            <input
              type="text"
              placeholder="Cari judul, deskripsi, toko..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="bulk-actions">
          <span>{selectedIds.size} item terpilih</span>
          <button onClick={clearSelection} className="btn-clear">
            Batal
          </button>
          <button onClick={() => handleBulkAction("activate")} className="btn-activate">
            <Check size={16} /> Aktifkan
          </button>
          <button onClick={() => handleBulkAction("deactivate")} className="btn-deactivate">
            Nonaktifkan
          </button>
          <button onClick={() => handleBulkAction("delete")} className="btn-delete">
            <Trash2 size={16} /> ❌
          </button>
        </div>
      )}

      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectedIds.size === sortedItems.length && sortedItems.length > 0}
                  onChange={selectAll}
                />
              </th>
              <th onClick={() => requestSort("id")}>
                ID {getSortIcon("id")}
              </th>
              <th onClick={() => requestSort("title")}>
                Judul {getSortIcon("title")}
              </th>
              <th onClick={() => requestSort("price")}>
                Harga {getSortIcon("price")}
              </th>
              <th onClick={() => requestSort("active")}>
                Status {getSortIcon("active")}
              </th>
              <th onClick={() => requestSort("storeid")}>
                Toko {getSortIcon("storeid")}
              </th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.length === 0 ? (
              <tr>
                <td colSpan={7} className="empty">
                  Tidak ada data
                </td>
              </tr>
            ) : (
              sortedItems.map((item) => {
                const isSelected = selectedIds.has(item.id);
                return (
                  <tr key={item.id} className={isSelected ? "selected-row" : ""}>
                    <td>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(item.id)}
                      />
                    </td>
                    <td>{item.id}</td>
                    <td className="title-cell">
                      <div>{item.title || "—"}</div>
                      <small>{(item.desc || "").substring(0, 80)}...</small>
                    </td>
                    <td>
                      Rp {Number(item.price || 0).toLocaleString("id-ID")}
                    </td>
                    <td>
                      <span className={`status ${item.active ? "active" : "inactive"}`}>
                        {item.active ? "Aktif" : "Nonaktif"}
                      </span>
                    </td>
                    <td>{item.storeid || "—"}</td>
                    <td className="actions-cell">
                      <Link to={`/update/${item.id}`} className="btn-edit">
                        ✏️
                      </Link>
                      <button
                        className="btn-delete-single"
                        onClick={() => {
                          if (window.confirm("Hapus item ini?")) {
                            // single delete logic here
                          }
                        }}
                      >
                        ❌
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <style jsx>{`
        .admin-page {
          padding: 16px 12px;
          max-width: 100%;
        }
        .header {
          margin-bottom: 16px;
        }
        .header h1 {
          margin: 0 0 12px;
          font-size: 1.6rem;
        }
        .controls {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          align-items: center;
        }
        .view-toggle {
          display: flex;
          gap: 8px;
        }
        .view-toggle button {
          padding: 8px 16px;
          border: 1px solid #ccc;
          border-radius: 20px;
          background: white;
          cursor: pointer;
        }
        .view-toggle button.active {
          background: #1976d2;
          color: white;
          border-color: #1976d2;
        }
        .search-box {
          flex: 1;
          min-width: 220px;
          display: flex;
          align-items: center;
          background: white;
          border: 1px solid #ddd;
          border-radius: 20px;
          padding: 8px 12px;
        }
        .search-box input {
          border: none;
          outline: none;
          flex: 1;
          margin-left: 8px;
        }

        .bulk-actions {
          background: #fff3e0;
          padding: 12px;
          border-radius: 8px;
          margin: 12px 0;
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          align-items: center;
        }
        .bulk-actions button {
          padding: 8px 16px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
        }
        .btn-clear { background: #e0e0e0; }
        .btn-activate { background: #4caf50; color: white; }
        .btn-deactivate { background: #ff9800; color: white; }
        .btn-delete { background: #fdfbfb; color: white; }

        .table-container {
          overflow-x: auto;
          max-height: 70vh;
          border: 1px solid #ddd;
          border-radius: 8px;
          background: white;
        }
        .admin-table {
          width: 100%;
          min-width: 950px;
          border-collapse: collapse;
        }
        th, td {
          padding: 12px 10px;
          text-align: left;
          border-bottom: 1px solid #f0f0f0;
        }
        th {
          background: #f5f5f5;
          font-weight: 600;
          cursor: pointer;
          user-select: none;
          white-space: nowrap;
        }
        th:hover {
          background: #e8e8e8;
        }
        .title-cell {
          max-width: 320px;
        }
        .title-cell div {
          font-weight: 500;
        }
        .title-cell small {
          color: #777;
          font-size: 0.85rem;
        }
        .status {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 0.85rem;
        }
        .status.active { background: #e8f5e9; color: #2e7d32; }
        .status.inactive { background: #ffebee; color: #c62828; }
        .actions-cell {
          white-space: nowrap;
        }
        .btn-edit, .btn-delete-single {
          padding: 6px 12px;
          margin-right: 8px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }
        .btn-edit { background: #ffffff; color: white; }
        .btn-delete-single { background: #ffffff; color: white; }

        .selected-row {
          background: #e3f2fd !important;
        }
        .empty {
          text-align: center;
          padding: 60px 20px;
          color: #777;
        }

        @media (max-width: 768px) {
          .controls { flex-direction: column; align-items: stretch; }
          .view-toggle { justify-content: center; }
        }
      `}</style>
    </div>
  );
};

export default AdminProduct;